const pendingEditCacheKey = (patientId, measurementId) =>
  `lorie:anthropometry:pending-edit:${patientId}:${measurementId}`;

const readRaw = (patientId, measurementId) => {
  if (!patientId || measurementId == null) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(
      pendingEditCacheKey(patientId, measurementId)
    );
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const readPendingEdit = (patientId, measurementId) => readRaw(patientId, measurementId);

export const hasPendingEdit = (patientId, measurementId) => {
  const pending = readRaw(patientId, measurementId);
  return Boolean(pending?.circumference || pending?.skinfold);
};

export const writePendingSection = (patientId, measurementId, section, data) => {
  if (!patientId || measurementId == null || !section) {
    return;
  }
  const current = readRaw(patientId, measurementId) || {};
  sessionStorage.setItem(
    pendingEditCacheKey(patientId, measurementId),
    JSON.stringify({
      ...current,
      [section]: data,
    })
  );
};

export const clearPendingEdit = (patientId, measurementId) => {
  if (!patientId || measurementId == null) {
    return;
  }
  sessionStorage.removeItem(pendingEditCacheKey(patientId, measurementId));
};

export const clearPendingSection = (patientId, measurementId, section) => {
  if (!patientId || measurementId == null || !section) {
    return;
  }
  const current = readRaw(patientId, measurementId);
  if (!current?.[section]) {
    return;
  }
  const next = { ...current };
  delete next[section];
  if (!next.circumference && !next.skinfold) {
    clearPendingEdit(patientId, measurementId);
    return;
  }
  sessionStorage.setItem(
    pendingEditCacheKey(patientId, measurementId),
    JSON.stringify(next)
  );
};

export const mergePendingIntoMeasurement = (measurement, pending) => {
  if (!measurement || !pending) {
    return measurement;
  }

  const merged = {
    ...measurement,
    circumference: measurement.circumference ? { ...measurement.circumference } : {},
    anthropometry: measurement.anthropometry ? { ...measurement.anthropometry } : {},
    metabolic_data: measurement.metabolic_data ? { ...measurement.metabolic_data } : {},
    custom_circumferences: measurement.custom_circumferences
      ? [...measurement.custom_circumferences]
      : [],
    skinfold_readings: measurement.skinfold_readings
      ? [...measurement.skinfold_readings]
      : measurement.skinfoldReadings
        ? [...measurement.skinfoldReadings]
        : [],
    selected_skinfold_protocols:
      measurement.selected_skinfold_protocols ||
      measurement.selectedSkinfoldProtocols ||
      [],
  };

  const circumferencePending = pending.circumference?.payload;
  if (circumferencePending) {
    if (circumferencePending.circumference) {
      merged.circumference = {
        ...merged.circumference,
        ...circumferencePending.circumference,
      };
    }
    if (circumferencePending.custom_circumferences) {
      merged.custom_circumferences = circumferencePending.custom_circumferences;
    }
    if (circumferencePending.metabolic_data) {
      merged.metabolic_data = {
        ...merged.metabolic_data,
        ...circumferencePending.metabolic_data,
      };
    }
    if (circumferencePending.anthropometry) {
      merged.anthropometry = {
        ...merged.anthropometry,
        ...circumferencePending.anthropometry,
      };
    }
  }

  const skinfoldPending = pending.skinfold?.payload;
  if (skinfoldPending) {
    if (skinfoldPending.skinfold_readings) {
      merged.skinfold_readings = skinfoldPending.skinfold_readings;
    }
    if (skinfoldPending.selected_skinfold_protocols) {
      merged.selected_skinfold_protocols = skinfoldPending.selected_skinfold_protocols;
    }
    if (skinfoldPending.anthropometry) {
      merged.anthropometry = {
        ...merged.anthropometry,
        ...skinfoldPending.anthropometry,
      };
    }
  }

  return merged;
};

const normalizeVisitDate = (visitDate) => {
  if (!visitDate) {
    return null;
  }
  if (typeof visitDate === "string" && visitDate.includes("T")) {
    return visitDate.split("T")[0];
  }
  return visitDate;
};

export const buildSavePayloadFromPending = ({
  measurement,
  pending,
  nutritionistId,
  patientId,
}) => {
  if (!measurement?.id || !pending) {
    return null;
  }

  const payload = {
    id: Number(measurement.id),
    patient_id: Number(patientId),
    nutritionist_id: nutritionistId,
    visit_date: normalizeVisitDate(measurement.visit_date),
  };

  const circumferencePending = pending.circumference?.payload;
  if (circumferencePending) {
    if (circumferencePending.circumference) {
      payload.circumference = circumferencePending.circumference;
      payload.replace_circumference = true;
    }
    if (circumferencePending.custom_circumferences) {
      payload.custom_circumferences = circumferencePending.custom_circumferences;
    }
    if (circumferencePending.metabolic_data) {
      payload.metabolic_data = circumferencePending.metabolic_data;
    }
    if (circumferencePending.anthropometry) {
      payload.anthropometry = circumferencePending.anthropometry;
    }
  }

  const skinfoldPending = pending.skinfold?.payload;
  if (skinfoldPending) {
    if (skinfoldPending.skinfold_readings) {
      payload.skinfold_readings = skinfoldPending.skinfold_readings;
    }
    if (skinfoldPending.selected_skinfold_protocols) {
      payload.selected_skinfold_protocols = skinfoldPending.selected_skinfold_protocols;
    }
    payload.replace_skinfold_data = true;

    if (skinfoldPending.anthropometry) {
      payload.anthropometry = {
        ...(payload.anthropometry || {}),
        ...skinfoldPending.anthropometry,
      };
    }
  }

  return payload;
};
